import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  MapPin,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Star,
  Globe,
  Filter,
  CheckCircle,
  Users,
  TrendingUp,
  ArrowRight,
  Store,
  Smartphone,
} from 'lucide-react';
import { getEventList, getCountryList } from '../stores/actions/eventsActions';
import { setCountry, resetPageAndHasMore } from '../stores/slices/eventSlice';
import { debounce, getCookie } from '../utils/utils';
import { request } from '../utils/request';
import { useHomeSearch } from '../components/layouts/layouts';
import Ticket from '../../../../public/assets/images/ticktcard.png';
import GoogleMapList from '../components/map/GoolgleMap';
// import {Traingirl} from "../../../../public/assets/images/traingirl.avif";

const detectUserCountry = () => {
  const langs = [];
  if (typeof navigator !== 'undefined') {
    if (Array.isArray(navigator.languages)) langs.push(...navigator.languages);
    if (navigator.language) langs.push(navigator.language);
    if (navigator.userLanguage) langs.push(navigator.userLanguage);
  }

  let displayNames;
  try {
    displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
  } catch (e) {
    return null;
  }

  for (const lang of langs) {
    const parts = String(lang).split('-');
    if (parts.length < 2) continue;
    const code = parts[parts.length - 1].toUpperCase();
    if (!/^[A-Z]{2}$/.test(code)) continue;
    try {
      const name = displayNames.of(code);
      if (name) return name;
    } catch (e) { }
  }
  return null;
};

const PLACEHOLDER_EVENT_IMAGE = '/assets/images/event-cover-paceholder.png';

const CITY_IMAGES = {
  Kingston: 'https://dobusinessjamaica.com/wp-content/uploads/2018/11/Jamaica_Waterfront2.jpg',
  'Montego Bay': 'https://images.unsplash.com/photo-1548574505-12caf0050b5b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  'Ocho Rios': 'https://images.unsplash.com/photo-1518331539958-385ee025b3fc?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  Negril: 'https://images.unsplash.com/photo-1542282811-943ef1a977c3?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
};

const CITY_IMAGE_POOL = [
  'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1533174000220-db981f42194c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1493514789931-586cb221d7a7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1506157786151-b8491531f063?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1504609813442-a8924e83f76e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1496843916299-590492c751f4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1571266028243-d220bc561be1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
];

const DEFAULT_CITY_IMAGE = CITY_IMAGE_POOL[0];

const hashString = (str) => {
  let hash = 0;
  const s = String(str || '');
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) - hash) + s.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

const pickCityImage = (cityName) => {
  if (CITY_IMAGES[cityName]) return CITY_IMAGES[cityName];
  if (!cityName) return DEFAULT_CITY_IMAGE;
  return CITY_IMAGE_POOL[hashString(cityName) % CITY_IMAGE_POOL.length];
};

const ClearFilterIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M18 6L6 18M6 6l12 12"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DATE_TABS = ['All', 'This Weekend', 'Next Weekend', 'This Month'];

const scrollToEventsSection = () => {
  const filters = document.getElementById('event-filters');
  const section = document.getElementById('events-section');
  const target = filters || section;
  if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const scrollToDiscoveryMap = () => {
  const el = document.getElementById('discovery-map');
  if (el) el.scrollIntoView({ behavior: 'smooth' });
};

const formatFilterDateLabel = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const FALLBACK_HERO_IMAGES = [
  'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80',
  'https://images.unsplash.com/photo-1533174000220-db981f42194c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80',
  'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80',
];

const toIsoDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const DatePickerDialog = ({ open, initialDate, onClose, onApply }) => {
  const today = new Date();

  const parseInitial = () => {
    if (initialDate) {
      const d = new Date(initialDate);
      if (!isNaN(d.getTime())) return d;
    }
    return today;
  };

  const [viewDate, setViewDate] = useState(parseInitial());
  const [selected, setSelected] = useState(
    initialDate ? new Date(initialDate) : null
  );
  const [hoveredDay, setHoveredDay] = useState(null);

  useEffect(() => {
    if (!open) return;

    const d = initialDate ? new Date(initialDate) : null;

    setSelected(d && !isNaN(d.getTime()) ? d : null);
    setViewDate(d && !isNaN(d.getTime()) ? d : today);
  }, [open, initialDate]);

  useEffect(() => {
    if (!open) return;

    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const monthLabel = viewDate.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const weekDays = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  const cells = [];

  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const isSameDay = (a, b) =>
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const todayDate = new Date();

  const goPrev = () => setViewDate(new Date(year, month - 1, 1));
  const goNext = () => setViewDate(new Date(year, month + 1, 1));

  const pickDay = (d) => {
    setSelected(new Date(year, month, d));
  };

  return (
    <div
      className="fixed inset-0 z-[300] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[350px] bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[2px] font-black text-gray-400">
              Select Date
            </p>
            <h3 className="text-base font-black text-black mt-1">
              {monthLabel}
            </h3>
          </div>

          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full border-none bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-bold"
          >
            ✕
          </button>
        </div>

        {/* Month Navigation */}
        <div className="flex items-center justify-between px-4 py-2">
          <button
            type="button"
            onClick={goPrev}
            className="w-8 h-8 rounded-full border border-gray-200 hover:border-[#ffea00] hover:bg-[#ffea00] flex items-center justify-center transition-all"
          >
            <ChevronLeft size={16} />
          </button>

          <span className="text-sm font-black text-gray-900">
            {monthLabel}
          </span>

          <button
            type="button"
            onClick={goNext}
            className="w-8 h-8 rounded-full border border-gray-200 hover:border-[#ffea00] hover:bg-[#ffea00] flex items-center justify-center transition-all"
          >
            <ChevronRight size={16} />
          </button>
        </div>

        {/* Week Days */}
        <div className="px-3">
          <div className="grid grid-cols-7 mb-1">
            {weekDays.map((day) => (
              <div
                key={day}
                className="text-center text-[10px] font-black text-gray-400 py-1"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar */}
          <div className="grid grid-cols-7 gap-1 pb-3">
            {cells.map((d, idx) => {
              if (!d) return <div key={idx} />;

              const cellDate = new Date(year, month, d);

              const isSelected = isSameDay(cellDate, selected);
              const isToday = isSameDay(cellDate, todayDate);
              const dayKey = `${year}-${month}-${d}`;
              const isHovered = hoveredDay === dayKey && !isSelected && !isToday;

              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => pickDay(d)}
                  onMouseEnter={() => {
                    if (!isSelected && !isToday) setHoveredDay(dayKey);
                  }}
                  onMouseLeave={() => setHoveredDay(null)}
                  style={{
                    border: isSelected ? 'none' : isToday ? '2px solid #ffea00' : 'none',
                  }}
                  className={`
                    h-9 w-9 mx-auto rounded-full text-sm font-bold
                    transition-all duration-200
                    ${isSelected
                      ? "bg-[#ffea00] text-black shadow-md"
                      : isToday
                        ? "text-black"
                        : `text-gray-700${isHovered ? " #ffea00" : ""}`
                    }
                  `}
                >
                  {d}
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 p-3">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="flex-1 h-10 rounded-lg border border-gray-200 text-sm font-black text-gray-600 hover:bg-gray-50"
            >
              Clear
            </button>

            <button
              type="button"
              onClick={() => {
                onApply(selected ? toIsoDate(selected) : "");
                onClose();
              }}
              className="flex-1 h-10 border-none rounded-lg bg-[#ffea00] hover:bg-[#f0dd00] text-black text-sm font-black"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const formatEventDate = (dateString) => {
  if (!dateString) return '';
  const opts = { weekday: 'short', month: 'short', day: '2-digit' };
  // Parse the Y-M-D parts directly and build a LOCAL date. Using
  // `new Date("2026-06-18")` parses as UTC midnight, which rolls back to the
  // previous day in negative-offset timezones — making the start date show a
  // day early. Splitting on 'T' first also tolerates full ISO timestamps.
  const [y, m, d] = String(dateString).split('T')[0].split('-').map(Number);
  if (y && m && d) {
    const local = new Date(y, m - 1, d);
    return `${local.toLocaleDateString('en-US', opts)}, ${local.getFullYear()}`;
  }
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return `${date.toLocaleDateString('en-US', opts)}, ${date.getFullYear()}`;
};

const Hero = ({
  country,
  countries,
  onCountryChange,
  searchValue,
  onSearchChange,
  onSearchSubmit,
  selectedDate,
  onDateChange,
  onFilterClick,
  slideImages,
  searchSectionRef,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [countryMenuOpen, setCountryMenuOpen] = useState(false);
  const [countryMenuPosition, setCountryMenuPosition] = useState(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [isMdScreen, setIsMdScreen] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches
  );
  const countryMenuRef = useRef(null);
  const countryDropdownRef = useRef(null);
  const sectionBorderRightStyle = isMdScreen ? { borderRight: '1px solid #e5e7eb' } : undefined;
  const formatDateLabel = (value) => {
    if (!value) return 'Any date';
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const slides = slideImages?.length ? slideImages : FALLBACK_HERO_IMAGES;

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (countryMenuRef.current?.contains(e.target)) return;
      if (countryDropdownRef.current?.contains(e.target)) return;
      setCountryMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (!countryMenuOpen || !countryMenuRef.current) {
      setCountryMenuPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      if (!countryMenuRef.current) return;
      const rect = countryMenuRef.current.getBoundingClientRect();
      const menuWidth = Math.max(rect.width, 224);
      const viewportPadding = 12;
      let left = rect.left;

      if (left + menuWidth > window.innerWidth - viewportPadding) {
        left = window.innerWidth - menuWidth - viewportPadding;
      }
      left = Math.max(viewportPadding, left);

      setCountryMenuPosition({
        top: rect.bottom + 8,
        left,
        width: menuWidth,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [countryMenuOpen, countries?.length]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = (e) => setIsMdScreen(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return (
    <div className="relative bg-gray-900 pt-20 pb-32 lg:pt-32 lg:pb-40">
      <div className="absolute inset-0 z-0 overflow-hidden bg-black">
        {slides.map((img, idx) => (
          <img
            key={`${img}-${idx}`}
            src={img}
            alt="Epic Event Background"
            className={`absolute inset-0 w-full h-full object-cover transition-all duration-1000 ease-in-out ${idx === currentSlide ? 'opacity-40 scale-105' : 'opacity-0 scale-100'
              }`}
            onError={({ currentTarget }) => {
              currentTarget.onerror = null;
              currentTarget.src = FALLBACK_HERO_IMAGES[0];
            }}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/20 to-gray-900"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mt-8">
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white tracking-tight mb-6 drop-shadow-2xl">
          Your Ticket to <span className=" text-4xl md:text-6xl lg:text-7xl font-extrabold  text-[#ffea00] drop-shadow-[0_0_25px_rgba(255,234,0,0.4)]">Entertainment</span>
        </h1>
        <p className="text-lg md:text-2xl text-gray-200 mb-12 max-w-3xl mx-auto font-medium drop-shadow-md">
          Discover the best events, parties, and experiences happening near you.
        </p>

        <form
          ref={searchSectionRef}
          onSubmit={(e) => { e.preventDefault(); onSearchSubmit(); }}
          className="max-w-5xl mx-auto bg-white rounded-3xl md:rounded-full shadow-2xl p-2 flex flex-col md:flex-row md:items-stretch"
        >
          <div
            style={sectionBorderRightStyle}
            className="w-full md:flex-1 md:min-w-0 flex items-center gap-3 px-4 sm:px-6 py-4 border-b md:border-b-0 border-gray-200"
          >
            <Search className="text-gray-400 flex-shrink-0" size={24} />
            <input
              type="text"
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search event name"
              className="w-full min-w-0 bg-transparent border-none focus:ring-0 text-gray-900 placeholder-gray-400 outline-none text-lg font-medium"
            />
          </div>

          <div
            ref={countryMenuRef}
            style={sectionBorderRightStyle}
            className="relative z-20 w-full md:flex-1 md:min-w-0 flex items-center gap-3 px-4 sm:px-6 py-4 border-b md:border-b-0 border-gray-200 cursor-pointer hover:bg-[#ffea00]/10 transition-colors md:rounded-full"
            onClick={() => setCountryMenuOpen((o) => !o)}
          >
            <MapPin className="text-[#ffea00] flex-shrink-0" size={24} />
            <div className="flex flex-1 min-w-0 items-center justify-between text-gray-700 font-medium text-lg">
              <span className="truncate text-lg font-bold text-gray-900">{country || 'Select country'}</span>
              <ChevronDown
                size={20}
                className={`flex-shrink-0 ml-2 transition-transform duration-200 ${countryMenuOpen ? 'rotate-180 text-[#ffea00]' : 'text-gray-400'}`}
              />
            </div>
          </div>

          {countryMenuOpen && countries?.length > 0 && countryMenuPosition && typeof document !== 'undefined' && createPortal(
            <div
              ref={countryDropdownRef}
              style={{
                position: 'fixed',
                top: countryMenuPosition.top,
                left: countryMenuPosition.left,
                width: countryMenuPosition.width,
                zIndex: 9999,
              }}
              className="overflow-hidden rounded-2xl border border-yellow-500/30 bg-white shadow-[0_20px_40px_rgba(0,0,0,0.22)]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-[#ffea00] px-4 py-2.5 border-b border-yellow-500/40">
                <p className="mb-0 text-[11px] font-black uppercase tracking-wider text-black">Select country</p>
              </div>
              <div className="max-h-56 overflow-y-auto py-1">
                {countries.map((c) => {
                  const isActive = country === c.country;
                  return (
                    <button
                      type="button"
                      key={c.country}
                      onClick={(e) => {
                        e.stopPropagation();
                        onCountryChange(c.country);
                        setCountryMenuOpen(false);
                      }}
                      className={`flex w-full items-center gap-2.5 border-none px-4 py-3 text-left text-sm font-bold transition-colors ${
                        isActive
                          ? 'bg-[#ffea00] text-black'
                          : 'bg-white text-gray-700 hover:bg-[#ffea00]/15 hover:text-black'
                      }`}
                    >
                      <MapPin size={14} className={`shrink-0 ${isActive ? 'text-black' : 'text-[#ffea00]'}`} />
                      <span className="truncate">{c.country}</span>
                      {isActive && <CheckCircle size={14} className="ml-auto shrink-0 text-black" />}
                    </button>
                  );
                })}
              </div>
            </div>,
            document.body
          )}

          <div
            style={sectionBorderRightStyle}
            className="w-full md:flex-1 md:min-w-0 flex items-center px-4 sm:px-6 py-4 border-b md:border-b-0 border-gray-200 cursor-pointer hover:bg-[#ffea00]/10 transition-colors"
          >
            <button
              type="button"
              className="flex w-full min-w-0 items-center gap-3 border-none bg-transparent p-0 text-left"
              onClick={() => setDatePickerOpen(true)}
            >
              <Calendar className="text-[#ffea00] flex-shrink-0" size={24} />
              <div className="flex flex-1 min-w-0 items-center justify-between text-gray-700 font-medium text-lg">
                <span className={`truncate text-lg font-medium ${selectedDate ? 'text-black' : ''}`}>
                  {formatDateLabel(selectedDate)}
                </span>
                {selectedDate ? (
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label="Clear date"
                    onClick={(e) => { e.stopPropagation(); onDateChange(''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onDateChange(''); } }}
                    className="text-gray-400 hover:text-black text-lg leading-none cursor-pointer flex-shrink-0 ml-2"
                  >
                    ×
                  </span>
                ) : (
                  <ChevronDown size={20} className="text-gray-400 flex-shrink-0 ml-2" />
                )}
              </div>
            </button>
          </div>

          <div className="w-full md:flex-1 md:min-w-0 flex items-center gap-2 p-2">
            <button
              type="button"
              onClick={onFilterClick}
              aria-label="Jump to event filters"
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 w-[55px] h-[60px] border-none rounded-full md:rounded-full transition-all flex items-center justify-center flex-shrink-0"
            >
              <Filter size={24} />
            </button>
            <button
              type="submit"
              className="flex-1 bg-[#ffea00] hover:bg-[#e6d300] text-black font-extrabold py-3 px-10 rounded-2xl md:rounded-full transition-all shadow-[0_0_20px_rgba(255,234,0,0.3)] hover:shadow-[0_0_30px_rgba(255,234,0,0.5)] flex items-center justify-center text-lg border-none min-w-0"
            >
              Search
            </button>
          </div>
        </form>
      </div>

      <DatePickerDialog
        open={datePickerOpen}
        initialDate={selectedDate}
        onClose={() => setDatePickerOpen(false)}
        onApply={(iso) => onDateChange(iso)}
      />
    </div>
  );
};

const EventListings = ({
  events,
  categories,
  loading,
  hasMore,
  page,
  activeFilter,
  country,
  countries,
  onCountryChange,
  onFilterChange,
  onLoadMore,
  onEventClick,
  selectedCity,
  onClearCity,
  selectedDate,
  onClearDate,
}) => {
  const loaderRef = useRef(null);
  const filterScrollRef = useRef(null);
  const [countryMenuOpen, setCountryMenuOpen] = useState(false);
  const [countryMenuPosition, setCountryMenuPosition] = useState(null);
  const [hoveredFilter, setHoveredFilter] = useState(null);
  const countryMenuRef = useRef(null);
  const countryDropdownRef = useRef(null);

  // On first mount `loading` is still false (the initial fetch hasn't been
  // dispatched yet) and `events` is empty — which would briefly flash the
  // "No events just yet" empty state before the default "All" results load.
  // Track whether a fetch has ever started so the empty state only shows after
  // a real load has completed with zero results.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  useEffect(() => {
    if (loading) setHasLoadedOnce(true);
  }, [loading]);

  const filterOptions = useMemo(() => ['All', ...(categories || [])], [categories]);

  // Keep the selected category centered in its horizontal strip.
  //
  // Horizontal ONLY, and never on the first run. scrollIntoView was doing both
  // of those wrong: even with block:'nearest' it scrolls every scrollable
  // ancestor — including the document — so on mount, before the visitor has
  // touched anything, it dragged the page down to #event-filters and past the
  // hero. On a phone, where the strip starts below the fold, that is the whole
  // hero gone.
  const filterStripSettledRef = useRef(false);
  useEffect(() => {
    const container = filterScrollRef.current;
    if (!container) return;

    // Mount is not a filter change: the default tab is already the right one,
    // so there is nothing to bring into view.
    if (!filterStripSettledRef.current) {
      filterStripSettledRef.current = true;
      return;
    }

    const activeBtn = container.querySelector('[data-filter-active="true"]');
    if (!activeBtn) return;

    // Measured from rects rather than offsetLeft: offsetLeft is relative to the
    // nearest positioned ancestor, which this container is not.
    const btn = activeBtn.getBoundingClientRect();
    const strip = container.getBoundingClientRect();
    const delta = (btn.left + btn.width / 2) - (strip.left + strip.width / 2);

    container.scrollTo({ left: container.scrollLeft + delta, behavior: 'smooth' });
  }, [activeFilter, filterOptions.length]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (countryMenuRef.current?.contains(e.target)) return;
      if (countryDropdownRef.current?.contains(e.target)) return;
      setCountryMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (!countryMenuOpen || !countryMenuRef.current) {
      setCountryMenuPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      if (!countryMenuRef.current) return;
      const rect = countryMenuRef.current.getBoundingClientRect();
      const menuWidth = 224;
      const viewportPadding = 12;
      let left = rect.left;

      if (left + menuWidth > window.innerWidth - viewportPadding) {
        left = window.innerWidth - menuWidth - viewportPadding;
      }
      left = Math.max(viewportPadding, left);

      setCountryMenuPosition({
        top: rect.bottom + 8,
        left,
        width: menuWidth,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [countryMenuOpen, countries?.length]);

  useEffect(() => {
    // Only paginate once there is more to load AND a first page has already
    // arrived. Without the `events.length` guard the sentinel fires "load more"
    // while the (list-clearing) first page is still in flight, which cancels
    // that request and re-triggers endlessly — a request every ~second.
    if (!hasMore || loading || events.length === 0) return;
    const currentLoader = loaderRef.current;
    if (!currentLoader) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          onLoadMore();
        }
      },
      { rootMargin: '100px' }
    );
    observer.observe(currentLoader);
    return () => observer.unobserve(currentLoader);
  }, [hasMore, loading, onLoadMore, events.length]);

  return (
    <div id="events-section" className="w-full min-w-0 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 scroll-mt-20 sm:scroll-mt-24">

      {(selectedCity || selectedDate) && (
        <div className="mb-6 flex flex-wrap items-center gap-2 sm:gap-3">
          <span className="w-full sm:w-auto text-[11px] font-black uppercase tracking-wider text-gray-400 sm:mr-1">
            Active filters
          </span>

          {selectedCity && (
            <span className="inline-flex max-w-full items-center gap-2 rounded-full border-2 border-[#ffea00] bg-[#ffea00]/10 py-1.5 pl-3 pr-1.5 text-sm font-bold text-gray-900 shadow-[0_2px_8px_rgba(255,234,0,0.25)]">
              <span className="inline-flex min-w-0 items-center gap-1.5">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#ffea00]">
                  <MapPin size={13} className="text-black" />
                </span>
                <span className="truncate">{selectedCity}</span>
              </span>
              <button
                type="button"
                aria-label="Clear city filter"
                onClick={onClearCity}
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-none bg-black text-[#ffea00] transition-transform hover:scale-105 active:scale-95"
              >
                <ClearFilterIcon />
              </button>
            </span>
          )}

          {selectedDate && (
            <span className="inline-flex max-w-full items-center gap-2 rounded-full border-2 border-[#ffea00] bg-[#ffea00]/10 py-1.5 pl-3 pr-1.5 text-sm font-bold text-gray-900 shadow-[0_2px_8px_rgba(255,234,0,0.25)]">
              <span className="inline-flex min-w-0 items-center gap-1.5">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#ffea00]">
                  <Calendar size={13} className="text-black" />
                </span>
                <span className="truncate">{formatFilterDateLabel(selectedDate)}</span>
              </span>
              <button
                type="button"
                aria-label="Clear date filter"
                onClick={onClearDate}
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-none bg-black text-[#ffea00] transition-transform hover:scale-105 active:scale-95"
              >
                <ClearFilterIcon />
              </button>
            </span>
          )}
        </div>
      )}

      <div
        id="event-filters"
        ref={filterScrollRef}
        className="w-full min-w-0 max-w-full overflow-x-auto scrollbar-hide pb-6 mb-8 overscroll-x-contain scroll-mt-20 sm:scroll-mt-24"
      >
        <div className="flex w-max min-w-full flex-nowrap gap-3 pr-1">
          {filterOptions.map((cat) => {
            const isActive = activeFilter === cat;
            const isHovered = hoveredFilter === cat;

            const borderColor = isActive || isHovered ? '#ffea00' : '#d1d5db';

            return (
              <button
                key={cat}
                type="button"
                data-filter-active={isActive ? 'true' : undefined}
                onClick={() => onFilterChange(cat)}
                onMouseEnter={() => setHoveredFilter(cat)}
                onMouseLeave={() => setHoveredFilter(null)}
                style={{ border: `2px solid ${borderColor}` }}
                className={`shrink-0 whitespace-nowrap px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-200 shadow-sm ${isActive
                  ? 'bg-[#ffea00] text-black shadow-[0_4px_15px_rgba(255,234,0,0.3)]'
                  : isHovered
                    ? 'bg-[#ffea00]/10 text-gray-900'
                    : 'bg-white text-gray-900'
                  }`}
              >
                {cat}
              </button>
            );
          })}
        </div>
      </div>


      <div className="flex flex-col md:flex-row justify-between items-baseline pb-4 mb-10 min-w-0"
        style={{ borderBottom: '1px solid #e5e7eb' }}
      >
        <h2 className="text-xl md:text-3xl font-black text-gray-900 tracking-tight mb-4 md:mb-0 flex items-center whitespace-nowrap">
          <TrendingUp className="text-black bg-[#ffea00] p-1.5 rounded-lg mr-3" size={32} />
          Trending in

          <div ref={countryMenuRef} className="relative ml-2">
            <button
              type="button"
              onClick={() => setCountryMenuOpen((o) => !o)}
              className="relative z-10 md:text-3xl text-xl font-black border-none bg-transparent text-black flex items-center cursor-pointer group focus:outline-none"
            >
              {country || 'Jamaica'}

              <ChevronDown
                size={28}
                className={`ml-1 transition-all duration-200 ${countryMenuOpen ? 'rotate-180 text-[#ffea00]' : 'text-gray-400 group-hover:text-black'}`}
              />

              <span className="absolute bottom-1 left-0 right-7 h-3 bg-[#ffea00] -z-10"></span>
            </button>

            {countryMenuOpen && countries?.length > 0 && countryMenuPosition && typeof document !== 'undefined' && createPortal(
              <div
                ref={countryDropdownRef}
                style={{
                  position: 'fixed',
                  top: countryMenuPosition.top,
                  left: countryMenuPosition.left,
                  width: countryMenuPosition.width,
                  zIndex: 9999,
                }}
                className="overflow-hidden rounded-2xl border border-yellow-500/30 bg-white shadow-[0_20px_40px_rgba(0,0,0,0.22)]"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="bg-[#ffea00] px-4 py-2.5 border-b border-yellow-500/40">
                  <p className="mb-0 text-[11px] font-black uppercase tracking-wider text-black">Select country</p>
                </div>
                <div className="max-h-56 overflow-y-auto py-1">
                  {countries.map((c) => {
                    const isActive = country === c.country;
                    return (
                      <button
                        type="button"
                        key={c.country}
                        onClick={(e) => {
                          e.stopPropagation();
                          onCountryChange(c.country);
                          setCountryMenuOpen(false);
                        }}
                        className={`flex w-full items-center gap-2.5 border-none px-4 py-3 text-left text-sm font-bold transition-colors ${
                          isActive
                            ? 'bg-[#ffea00] text-black'
                            : 'bg-white text-gray-700 hover:bg-[#ffea00]/15 hover:text-black'
                        }`}
                      >
                        <MapPin size={14} className={`shrink-0 ${isActive ? 'text-black' : 'text-[#ffea00]'}`} />
                        <span className="truncate">{c.country}</span>
                        {isActive && <CheckCircle size={14} className="ml-auto shrink-0 text-black" />}
                      </button>
                    );
                  })}
                </div>
              </div>,
              document.body
            )}
          </div>
        </h2>

        <div className="w-full min-w-0 max-w-full overflow-x-auto md:w-auto">
          <div className="flex flex-nowrap space-x-6">
          {DATE_TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => onFilterChange(tab)}
              className={`relative shrink-0 pb-2 text-sm bg-transparent font-black whitespace-nowrap transition-all border-b-[4px] ${activeFilter === tab
                ? 'text-black border-transparent'
                : 'border-transparent text-gray-400 hover:text-black hover:border-b-gray-300'
                }`}
            >
              {tab.toUpperCase()}

              {activeFilter === tab && (
                <span className="absolute left-0 right-0 bottom-0 h-1 bg-[#ffea00]"></span>
              )}
            </button>
          ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
        {events.map((event, index) => (
          <div
            // Key by the event's stable identity (not the array index): the id
            // alone means a duplicate would collapse / surface React's
            // duplicate-key warning instead of silently rendering twice.
            key={event.id ?? event.slug ?? `event-${index}`}
            onClick={() => onEventClick(event)}
            className="group bg-white w-full max-w-md mx-auto sm:max-w-none rounded-b-2xl overflow-hidden transition-all duration-300 flex flex-col h-full cursor-pointer relative animate-pop-in hover:-translate-y-1"
            style={{
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              border: '1px solid #f3f4f6',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow =
                '0 20px 40px rgba(0,0,0,0.18)';
              e.currentTarget.style.border =
                '1px solid #e5e7eb';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow =
                '0 1px 3px rgba(0,0,0,0.08)';
              e.currentTarget.style.border =
                '1px solid #f3f4f6';
            }}
          >
            <div className="relative aspect-[16/9] w-full overflow-hidden bg-gray-100 shrink-0">
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent group-hover:from-black/50 transition-colors z-10"></div>
              <img
                src={event.horizontal_flyer || PLACEHOLDER_EVENT_IMAGE}
                alt={event.name}
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null;
                  currentTarget.src = PLACEHOLDER_EVENT_IMAGE;
                }}
                className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 ease-out"
              />

              {(event.genre || event.category) && (
                <div className="absolute bottom-4 right-4 z-20 flex flex-col items-end">
                  <span className="bg-[#ffea00] text-black text-xs font-black px-3 py-1.5 rounded-md uppercase tracking-wider shadow-lg">
                    {event.genre || event.category}
                  </span>
                </div>
              )}
            </div>

            <div className="p-4 sm:p-5 flex flex-col flex-grow">
              <h3 className="text-lg sm:text-xl font-black text-gray-900 mb-3 line-clamp-2 leading-tight group-hover:underline decoration-[#ffea00] decoration-4 underline-offset-2 transition-all">
                {event.name}
              </h3>

              <div className="flex flex-col space-y-2.5 mt-auto">
                <div className="flex items-center text-gray-800 text-sm font-bold">
                  <div className="bg-[#ffea00]/20 p-1 rounded flex justify-center items-center mr-3"><Calendar size={16} className="text-black" /></div>
                  <span>{formatEventDate(event.date)}</span>
                </div>
                <div className="flex items-center text-gray-800 text-sm font-bold ">
                  <div className="bg-[#ffea00]/20 p-1 rounded mr-3 mt-0.5 flex justify-center items-center"><MapPin size={16} className="text-black" /></div>
                  <span className="line-clamp-1">{event.venueaddress || event.location}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div ref={loaderRef}>
        {(loading || !hasLoadedOnce) && (
          <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mt-8 animate-pop-in">
            {[1, 2, 3].map((n) => (
              <div key={n} className="bg-white max-w-md mx-auto sm:max-w-none w-full rounded-b-2xl overflow-hidden shadow-sm border border-gray-100 flex flex-col h-full animate-pulse">
                <div className="aspect-[16/9] bg-gray-200 w-full relative shrink-0">
                  <div className="absolute bottom-4 right-4 w-16 h-6 bg-gray-300 rounded-md"></div>
                </div>
                <div className="p-5 sm:p-6 flex flex-col flex-grow">
                  <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                  <div className="h-6 bg-gray-200 rounded w-1/2 mb-auto"></div>
                  <div className="flex flex-col space-y-3 mt-6">
                    <div className="flex items-center">
                      <div className="w-6 h-6 bg-gray-200 rounded mr-3"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-6 h-6 bg-gray-200 rounded mr-3"></div>
                      <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {hasLoadedOnce && !loading && events.length === 0 && (
        <div className="mt-10 flex flex-col items-center justify-center text-center py-16 px-6 animate-pop-in">
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-[#ffea00]/15 flex items-center justify-center mb-6">
            <Calendar className="text-black" size={40} />
          </div>
          <h3 className="text-2xl sm:text-3xl font-black text-gray-900 mb-3">
            No events just yet
          </h3>
          <p className="text-gray-500 font-medium max-w-md mb-8 leading-relaxed">
            {selectedCity
              ? `We couldn't find any events in ${selectedCity} right now. Try a different city or check back soon — new events are added all the time!`
              : `There are no events matching your search in ${country || 'this area'} right now. Try adjusting your filters or check back soon — new events are added all the time!`}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            {selectedCity ? (
              <button
                type="button"
                onClick={onClearCity}
                className="bg-[#ffea00] hover:bg-[#e6d300] text-black font-black py-3 px-8 rounded-full transition-all shadow-[0_0_20px_rgba(255,234,0,0.3)] hover:shadow-[0_0_30px_rgba(255,234,0,0.5)] border-none"
              >
                Clear city filter
              </button>
            ) : selectedDate ? (
              <button
                type="button"
                onClick={onClearDate}
                className="bg-[#ffea00] hover:bg-[#e6d300] text-black font-black py-3 px-8 rounded-full transition-all shadow-[0_0_20px_rgba(255,234,0,0.3)] hover:shadow-[0_0_30px_rgba(255,234,0,0.5)] border-none"
              >
                Clear date filter
              </button>
            ) : (
              activeFilter !== 'All' && (
                <button
                  type="button"
                  onClick={() => onFilterChange('All')}
                  className="bg-[#ffea00] hover:bg-[#e6d300] text-black font-black py-3 px-8 rounded-full transition-all shadow-[0_0_20px_rgba(255,234,0,0.3)] hover:shadow-[0_0_30px_rgba(255,234,0,0.5)] border-none"
                >
                  View all events
                </button>
              )
            )}
            <a
              href="https://apsession.com/manager/event-setup"
              target="_blank"
              rel="noopener noreferrer"
              className="text-black font-black text-center py-3 px-8 rounded-full transition-all inline-flex items-center justify-center"
              style={{ border: '2px solid #e5e7eb' }}
            >
              Host an event <ArrowRight className="ml-2" size={18} />
            </a>
          </div>
        </div>
      )}

      {!loading && events.length > 0 && !hasMore && (
        <div className="mt-16 text-center">
          <div className="inline-flex items-center bg-gray-100 px-6 py-3 rounded-full">
            <CheckCircle className="text-green-500 mr-2" size={20} />
            <span className="text-gray-600 font-bold text-sm">You've seen all upcoming events</span>
          </div>
        </div>
      )}
    </div>
  );
};

const DiscoveryActionButton = ({ onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    className="group relative shrink-0 self-center overflow-hidden rounded-lg border-none bg-white px-3 py-2.5 text-sm font-black text-black shadow-[0_1px_3px_rgba(0,0,0,0.08)] transition-all duration-200 after:pointer-events-none after:absolute after:bottom-1.5 after:left-3 after:right-3 after:h-0.5 after:rounded-full after:bg-[#ffea00] after:content-[''] hover:bg-[#ffea00] hover:shadow-[0_4px_14px_rgba(255,234,0,0.35)] sm:px-3.5 sm:text-base"
  >
    <span className="relative z-10 inline-flex items-center gap-1.5">
      {label}
      <ArrowRight size={14} className="shrink-0 transition-transform duration-200 group-hover:translate-x-0.5" />
    </span>
  </button>
);

const DiscoverySection = ({ cities, country, selectedCity, onCityClick, onExploreAll }) => (
  <div className="bg-gray-50 py-16 sm:py-24 border-y border-gray-100">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

      <div className="text-center mb-12 sm:mb-16">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 tracking-tight mb-4">Discover & Connect</h2>
        <p className="text-lg sm:text-xl text-gray-600 font-medium">Find exciting events near you and explore top destinations.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 lg:items-start">
        <div className="flex min-w-0 flex-col">
          <div className="mb-6 flex min-h-[3.25rem] items-center justify-between gap-3 border-b border-gray-200 pb-4 sm:mb-8">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#ffea00] shadow-sm">
                <Globe className="text-black" size={20} />
              </div>
              <h3 className="mb-0 truncate text-xl font-black text-gray-900 sm:text-2xl">What&apos;s Near You</h3>
            </div>
            <DiscoveryActionButton onClick={scrollToDiscoveryMap} label="Open Map" />
          </div>

          <div id="discovery-map" className="w-full h-[300px] sm:h-[400px] md:h-[450px] bg-gray-200 rounded-[1.5rem] sm:rounded-[2rem] overflow-hidden relative shadow-inner border border-gray-200">
            <GoogleMapList />
          </div>
        </div>

        <div className="flex min-w-0 flex-col">
          <div className="mb-6 flex min-h-[3.25rem] items-center justify-between gap-3 border-b border-gray-200 pb-4 sm:mb-8">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#ffea00] shadow-sm">
                <MapPin className="text-black" size={20} />
              </div>
              <h3 className="mb-0 truncate text-xl font-black text-gray-900 sm:text-2xl">Trending Cities</h3>
            </div>
            <DiscoveryActionButton onClick={onExploreAll} label="Explore All" />
          </div>

          <div className="flex space-x-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4 -mx-4 px-4 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-2 sm:space-x-0 sm:gap-6">
            {cities.map((city) => {
              const isActive = selectedCity && selectedCity.toLowerCase() === city.name.toLowerCase();
              return (
                <div
                  key={city.name}
                  onClick={() => onCityClick(city.name)}
                  className={`relative flex-none w-[75vw] max-w-[280px] sm:max-w-none sm:w-full snap-start aspect-[4/3] rounded-[1.5rem] sm:rounded-[2rem] overflow-hidden shadow-sm hover:shadow-xl transition-all cursor-pointer group mb-0 sm:mb-2 ${isActive ? 'ring-4 ring-[#ffea00]' : ''}`}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent z-10 group-hover:from-black/90 transition-colors"></div>
                  <img
                    src={city.image}
                    alt={city.name}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700 ease-out"
                    onError={({ currentTarget }) => {
                      currentTarget.onerror = null;
                      currentTarget.src = DEFAULT_CITY_IMAGE;
                    }}
                  />
                  <div className="absolute bottom-4 sm:bottom-5 left-4 sm:left-5 z-20 pr-4">
                    <h4 className="text-lg sm:text-xl md:text-2xl font-black text-white mb-2 group-hover:text-[#ffea00] transition-colors leading-tight">{city.name}</h4>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onCityClick(city.name); }}
                      className="text-black font-black text-[10px] sm:text-xs bg-[#ffea00] hover:bg-[#e6d300] px-2.5 py-1 rounded-md inline-block uppercase shadow-sm transition-colors"
                    >
                      {city.count > 0
                        ? `${city.count} Active ${city.count === 1 ? 'Event' : 'Events'}`
                        : 'Explore Events'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>

    </div>
  </div>
);

const SellerCTA = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const features = [
    {
      id: 'online',
      icon: <Globe className="text-[#ffea00] sm:w-9 sm:h-9" size={32} />,
      title: "Online Ticket Hub",
      desc: "Fast, secure checkout optimized for all mobile and desktop devices.",
      bullets: ["Custom event URLs", "Multi-currency support", "Advanced fraud protection"]
    },
    {
      id: 'outlets',
      icon: <Store className="text-[#ffea00] sm:w-9 sm:h-9" size={32} />,
      title: "Digital Outlets",
      desc: "Make tickets available in physical partner stores directly from the app.",
      bullets: ["70+ Jamaica locations", "Instant ticket restocks", "No printed tickets needed"]
    },
    {
      id: 'ambassadors',
      icon: <Users className="text-[#ffea00] sm:w-9 sm:h-9" size={32} />,
      title: "Ambassadors",
      desc: "Mobilize your network to sell tickets on your behalf anywhere.",
      bullets: ["Sell from smartphones", "Secure cash collection", "Access verified top sellers"]
    },
    {
      id: 'delivery',
      icon: <Smartphone className="text-[#ffea00] sm:w-9 sm:h-9" size={32} />,
      title: "Frictionless Delivery",
      desc: "Direct-to-device ticket delivery ensuring you bypass spam folders.",
      bullets: ["Email ticket delivery", "WhatsApp ticket delivery", "SMS ticket delivery"]
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % features.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [features.length]);

  return (
    <div className="py-12 sm:py-16 md:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="bg-[#0a0a0a] rounded-[2rem] md:rounded-[3rem] overflow-hidden shadow-2xl relative">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] sm:w-[800px] sm:h-[800px] bg-[#ffea00]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none"></div>

        <div className="p-6 sm:p-10 md:p-16 lg:p-20 relative z-10">

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">

            <div className="z-10">
              <div className="inline-block bg-[#ffea00]/10 text-[#ffea00] font-black px-4 py-1.5 rounded-full text-xs sm:text-sm mb-4 sm:mb-6 border border-[#ffea00]/20 tracking-wide uppercase">
                For Organizers
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-4 sm:mb-6 leading-tight tracking-tight">
                List Your Event.<br />
                <span className="text-[#ffea00]  text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-4 sm:mb-6 leading-tight tracking-tight">Sell Out Faster.</span>
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-gray-400 font-medium mb-8 sm:mb-10 leading-relaxed max-w-xl">
                Maximize your event's ticketing reach by seamlessly selling tickets online at apsession.com, offline via digital ticket outlets and mobile ticket ambassadors, and at your event gate.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <a href="https://apsession.com/manager/event-setup" target="_blank" rel="noopener noreferrer" className="bg-[#ffea00] hover:bg-[#e6d300] text-black text-center font-black py-3.5 sm:py-4 px-8 sm:px-10 rounded-full transition-transform hover:-translate-y-1 shadow-[0_0_20px_rgba(255,234,0,0.2)] text-base sm:text-lg flex items-center justify-center w-full sm:w-max">
                  Create Your Event <ArrowRight className="ml-2" size={20} />
                </a>
                <a
                  href="https://apsession.com/manager/home"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white font-black text-center
                   py-3.5 sm:py-4 px-8 sm:px-10 rounded-full transition-all duration-200 text-base sm:text-lg flex items-center justify-center w-full sm:w-max"
                  style={{
                    border: '2px solid rgba(255,255,255,0.2)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.setProperty('background-color', 'rgba(255,255,255,0.1)', 'important');
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.removeProperty('background-color');
                  }}
                >
                  How it works
                </a>
              </div>
            </div>

            <div
              className="relative w-full min-h-[500px] sm:min-h-[520px] md:h-[500px] bg-white/5 rounded-[2rem] sm:rounded-[3rem] overflow-hidden shadow-inner group z-10"
              style={{
                border: '1px solid rgba(255,255,255,0.1)',
              }}
            >
              {features.map((feature, idx) => (
                <div
                  key={feature.id}
                  className={`absolute inset-0 p-6 sm:p-10 md:p-14 flex flex-col justify-center transition-all duration-700 ease-in-out ${idx === currentSlide ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8 pointer-events-none'
                    }`}
                >
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#ffea00]/10 rounded-2xl sm:rounded-3xl flex items-center justify-center mb-6 sm:mb-8 shadow-sm shrink-0">
                    {feature.icon}
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-black text-white mb-3 sm:mb-4">{feature.title}</h3>
                  <p className="text-gray-400 text-base sm:text-lg mb-6 sm:mb-8 font-medium leading-relaxed max-w-sm">{feature.desc}</p>
                  <ul className="space-y-3 sm:space-y-4 p-0">
                    {feature.bullets.map((bullet, i) => (
                      <li key={i} className="flex items-center text-sm sm:text-base text-gray-300 font-bold">
                        <CheckCircle size={20} className="text-[#ffea00] mr-3 sm:mr-4 flex-shrink-0" />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              <div className="absolute bottom-6 sm:bottom-10 left-0 right-0 flex justify-center space-x-3 z-20">
                {features.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentSlide(idx)}
                    className={`h-2.5 rounded-full transition-all border-none duration-300 ${idx === currentSlide ? 'w-8 bg-[#ffea00]' : 'w-2.5 bg-white/20 hover:bg-white/40'}`}
                  />
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

const AppPromo = () => (
  <div className="bg-white py-16 sm:py-24 mt-8 sm:mt-12 border-t border-gray-100">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row items-center">

        <div className="md:w-1/2 mb-16 md:mb-0 pr-0 md:pr-16">
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6 tracking-tight leading-tight">
            Apsession moves <span className="relative z-10 text-4xl md:text-5xl">with you.<span className="absolute  bottom-2 left-0 w-full h-4 bg-[#ffea00] -z-10"></span></span>
          </h2>
          <p className="text-gray-600 mb-8 text-lg leading-relaxed font-medium">
            Use Apsession to discover a world of exciting events near you. Parties, nightlife, concerts, and festivals are just a tap away. Purchase tickets with our fast, secure mobile checkout and never miss your next epic experience.
          </p>

          <div className="flex text-[#ffea00] mb-8 space-x-1 drop-shadow-sm">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={28} fill="currentColor" />
            ))}
            <span className="ml-3 text-gray-900 font-bold text-lg">4.9/5 on App Store</span>
          </div>

          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 mb-8">
            <a href="https://play.google.com/store/apps/details?id=com.apsession.air.apsessionuser" target="_blank" rel="noopener noreferrer" className="bg-black text-white px-6 py-3 rounded-2xl flex items-center justify-center hover:bg-gray-800 transition-colors shadow-lg">
              <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Get it on Google Play" className="h-8" />
            </a>
            <a href="https://apps.apple.com/us/app/apsession/id802888811" target="_blank" rel="noopener noreferrer" className="bg-black text-white px-6 py-3 rounded-2xl flex items-center justify-center hover:bg-gray-800 transition-colors shadow-lg">
              <img src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg" alt="Download on the App Store" className="h-8" />
            </a>
          </div>
        </div>

        <div className="md:w-1/2 relative flex justify-center">
          <div className="absolute inset-0 bg-[#ffea00] rounded-full blur-[100px] opacity-40 w-3/4 h-3/4 m-auto"></div>
          <img
            // src={Traingirl}
            src="https://images.unsplash.com/photo-1573164713988-8665fc963095?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
            alt="Happy user with app"
            className="relative z-10 w-full max-w-md rounded-[3rem] object-cover h-[450px] sm:h-[550px]"
            style={{
              boxShadow: '0 20px 50px rgba(0,0,0,0.15)',
              border: '10px solid white',
            }}
          />        <div className="absolute bottom-10 sm:bottom-16 left-0 md:-left-12 bg-white p-3 rounded-3xl shadow-2xl z-20 w-48 sm:w-56 rotate-[-6deg] border border-gray-100">
            <div className="bg-gray-50 h-72 sm:h-80 rounded-2xl overflow-hidden relative border border-gray-100">
              <img src={Ticket} className="w-full h-24 sm:h-32 object-cover opacity-90" alt="app event header" />
              <div className="px-4 py-2 bg-white text-center rounded-t-2xl absolute bottom-0 w-full shadow-[0_-10px_20px_rgba(0,0,0,0.05)]">
                <p className="font-black text-sm mb-2 text-gray-900">Admit One</p>
                <div className="w-24 h-24 sm:w-28 sm:h-28 mx-auto bg-[url('https://upload.wikimedia.org/wikipedia/commons/d/d0/QR_code_for_mobile_English_Wikipedia.svg')] bg-cover rounded-lg mb-2 opacity-80"></div>
                <p className="text-[11px] font-bold text-gray-400">#APS-123456</p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
);

export default function Home() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const country = useSelector((state) => state.event.country);
  const eventList = useSelector((state) => state.event.eventList);
  // Use the event-list-specific loading flag (flips false atomically with the
  // event data) rather than the global common.loading, which turns off a tick
  // before the list updates and briefly flashed the "No events" empty state.
  const loading = useSelector((state) => state.event.eventsLoading);
  const eventType = useSelector((state) => state.event.eventType);
  const page = useSelector((state) => state.event.page);
  const hasMore = useSelector((state) => state.event.hasMore);
  const getCountryFeeListDetails = useSelector((state) => state.event.getCountryFeeListDetails);

  const [searchValue, setSearchValue] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [cityCounts, setCityCounts] = useState([]);
  const heroSearchRef = useRef(null);
  const homeSearch = useHomeSearch();
  const registerSearch = homeSearch?.registerSearch;
  const unregisterSearch = homeSearch?.unregisterSearch;
  const setShowMobileHeaderSearch = homeSearch?.setShowMobileHeaderSearch;
  const setShowDesktopHeaderSearch = homeSearch?.setShowDesktopHeaderSearch;

  // Holds the most recent getEventList dispatch so a new fetch can abort the
  // previous in-flight one. This stops rapid filter/search/city clicks from
  // firing (and leaving pending) multiple overlapping requests to the server.
  const eventListReqRef = useRef(null);
  const runGetEventList = useCallback(
    (params) => {
      if (eventListReqRef.current) {
        eventListReqRef.current.abort();
      }
      const promise = dispatch(getEventList(params));
      eventListReqRef.current = promise;
      return promise;
    },
    [dispatch]
  );

  useEffect(() => {
    if (!getCountryFeeListDetails || getCountryFeeListDetails.length === 0) {
      dispatch(getCountryList());
    }
  }, [dispatch, getCountryFeeListDetails]);

  useEffect(() => {
    if (getCookie('_global_country')) return;
    if (!Array.isArray(getCountryFeeListDetails) || getCountryFeeListDetails.length === 0) return;

    const detected = detectUserCountry();
    if (!detected) return;

    const match = getCountryFeeListDetails.find(
      (c) => c.country && c.country.toLowerCase() === detected.toLowerCase()
    );
    if (match && match.country !== country) {
      dispatch(setCountry(match.country));
    }
  }, [dispatch, getCountryFeeListDetails, country]);

  useEffect(() => {
    setSelectedCity('');
    dispatch(resetPageAndHasMore());
    runGetEventList({
      eventType: 'All',
      page: 1,
      country,
      search: searchValue || undefined,
      date: selectedDate || undefined,
    });
  }, [dispatch, country]);

  useEffect(() => {
    let cancelled = false;
    request
      .get('/event/city-counts', { params: { country, limit: 12 } })
      .then((res) => {
        if (cancelled) return;
        const result = res?.data?.result || [];
        setCityCounts(result);
      })
      .catch(() => {
        if (!cancelled) setCityCounts([]);
      });
    return () => { cancelled = true; };
  }, [country]);

  const countryData = useMemo(() => {
    if (!Array.isArray(getCountryFeeListDetails)) return null;
    return getCountryFeeListDetails.find((item) => item.country === country) || null;
  }, [getCountryFeeListDetails, country]);

  const categories = countryData?.event_categories || [];
  const cityList = countryData?.city || [];

  const trendingCities = useMemo(() => {
    if (cityCounts && cityCounts.length > 0) {
      return cityCounts.slice(0, 4).map((row) => ({
        name: row.city,
        count: Number(row.count) || 0,
        image: row.image_url || pickCityImage(row.city),
      }));
    }
    const source = cityList?.length ? cityList : Object.keys(CITY_IMAGES);
    return source.slice(0, 4).map((cityName) => ({
      name: cityName,
      count: 0,
      image: pickCityImage(cityName),
    }));
  }, [cityCounts, cityList]);

  const heroSlideImages = useMemo(() => {
    const fromEvents = (eventList || [])
      .map((e) => e?.horizontal_flyer)
      .filter(Boolean)
      .slice(0, 3);
    return fromEvents.length ? fromEvents : FALLBACK_HERO_IMAGES;
  }, [eventList]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value, dateValue, cityValue, typeValue) => {
        dispatch(resetPageAndHasMore());
        runGetEventList({
          search: value,
          eventType: typeValue || 'All',
          page: 1,
          country,
          date: dateValue || undefined,
          city: cityValue || undefined,
        });
      }, 500),
    [dispatch, country, runGetEventList]
  );

  const handleSearchChange = useCallback(
    (value) => {
      setSearchValue(value);
      debouncedSearch(value, selectedDate, selectedCity, eventType);
    },
    [debouncedSearch, selectedDate, selectedCity, eventType]
  );

  const handleSearchSubmit = useCallback(() => {
    dispatch(resetPageAndHasMore());
    runGetEventList({
      search: searchValue,
      eventType: eventType || 'All',
      page: 1,
      country,
      date: selectedDate || undefined,
      city: selectedCity || undefined,
    });
    scrollToEventsSection();
  }, [dispatch, searchValue, eventType, country, selectedDate, selectedCity, runGetEventList]);

  const handleDateChange = useCallback(
    (value) => {
      setSelectedDate(value);
      dispatch(resetPageAndHasMore());
      runGetEventList({
        eventType: eventType || 'All',
        page: 1,
        country,
        search: searchValue || undefined,
        date: value || undefined,
        city: selectedCity || undefined,
      });
      scrollToEventsSection();
    },
    [dispatch, eventType, country, searchValue, selectedCity, runGetEventList]
  );

  const handleClearDate = useCallback(() => {
    handleDateChange('');
  }, [handleDateChange]);

  useEffect(() => {
    if (!unregisterSearch) return undefined;
    return () => unregisterSearch();
  }, [unregisterSearch]);

  useEffect(() => {
    if (!registerSearch) return;
    registerSearch({
      searchValue,
      onSearchChange: handleSearchChange,
      onSearchSubmit: handleSearchSubmit,
      country,
    });
  }, [registerSearch, searchValue, country, handleSearchChange, handleSearchSubmit]);

  useEffect(() => {
    if (!setShowMobileHeaderSearch) return undefined;

    const mq = window.matchMedia('(min-width: 769px)');
    let observer = null;

    const attachObserver = (searchEl) => {
      if (!searchEl) return;
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      if (mq.matches) {
        setShowMobileHeaderSearch(false);
        return;
      }
      observer = new IntersectionObserver(
        ([entry]) => {
          setShowMobileHeaderSearch(!entry.isIntersecting);
        },
        { threshold: 0, rootMargin: '-80px 0px 0px 0px' }
      );
      observer.observe(searchEl);
    };

    const syncObserver = () => {
      attachObserver(heroSearchRef.current);
    };

    syncObserver();

    const refCheckId = window.requestAnimationFrame(syncObserver);

    mq.addEventListener('change', syncObserver);
    return () => {
      window.cancelAnimationFrame(refCheckId);
      mq.removeEventListener('change', syncObserver);
      if (observer) observer.disconnect();
      setShowMobileHeaderSearch(false);
    };
  }, [setShowMobileHeaderSearch]);

  useEffect(() => {
    if (!setShowDesktopHeaderSearch) return undefined;

    const mq = window.matchMedia('(min-width: 1024px)');
    let observer = null;

    const attachObserver = (searchEl) => {
      if (!searchEl) return;
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      if (!mq.matches) {
        setShowDesktopHeaderSearch(false);
        return;
      }
      observer = new IntersectionObserver(
        ([entry]) => {
          setShowDesktopHeaderSearch(!entry.isIntersecting);
        },
        { threshold: 0, rootMargin: '-80px 0px 0px 0px' }
      );
      observer.observe(searchEl);
    };

    const syncObserver = () => {
      attachObserver(heroSearchRef.current);
    };

    syncObserver();

    const refCheckId = window.requestAnimationFrame(syncObserver);

    mq.addEventListener('change', syncObserver);
    return () => {
      window.cancelAnimationFrame(refCheckId);
      mq.removeEventListener('change', syncObserver);
      if (observer) observer.disconnect();
      setShowDesktopHeaderSearch(false);
    };
  }, [setShowDesktopHeaderSearch]);

  const handleCountryChange = (newCountry) => {
    dispatch(setCountry(newCountry));
  };

  const handleFilterChange = useCallback(
    (filter) => {
      dispatch(resetPageAndHasMore());
      runGetEventList({ eventType: filter, page: 1, country, search: searchValue || undefined, date: selectedDate || undefined, city: selectedCity || undefined });
      scrollToEventsSection();
    },
    [dispatch, country, searchValue, selectedDate, selectedCity, runGetEventList]
  );

  const handleCityClick = useCallback(
    (cityName) => {
      const next = selectedCity && selectedCity.toLowerCase() === cityName.toLowerCase() ? '' : cityName;
      setSelectedCity(next);
      dispatch(resetPageAndHasMore());
      runGetEventList({
        eventType: eventType || 'All',
        page: 1,
        country,
        search: searchValue || undefined,
        date: selectedDate || undefined,
        city: next || undefined,
      });
      const el = document.getElementById('events-section');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    },
    [dispatch, country, eventType, searchValue, selectedDate, selectedCity, runGetEventList]
  );

  const handleExploreAll = useCallback(() => {
    setSelectedCity('');
    dispatch(resetPageAndHasMore());
    runGetEventList({
      eventType: eventType || 'All',
      page: 1,
      country,
      search: searchValue || undefined,
      date: selectedDate || undefined,
    });
    const el = document.getElementById('events-section');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }, [dispatch, country, eventType, searchValue, selectedDate, runGetEventList]);

  const handleLoadMore = useCallback(() => {
    if (!hasMore || loading) return;
    runGetEventList({ eventType, page: page + 1, country, search: searchValue || undefined, date: selectedDate || undefined, city: selectedCity || undefined });
  }, [dispatch, hasMore, loading, eventType, page, country, searchValue, selectedDate, selectedCity, runGetEventList]);

  const handleEventClick = (event) => {
    if (event?.slug) navigate(`/events/${event.slug}`);
  };

  return (
    <div className="bg-white font-sans text-gray-900 selection:bg-[#ffea00] selection:text-black flex flex-col">
      <Hero
        country={country}
        countries={getCountryFeeListDetails || []}
        onCountryChange={handleCountryChange}
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        onSearchSubmit={handleSearchSubmit}
        selectedDate={selectedDate}
        onDateChange={handleDateChange}
        onFilterClick={scrollToEventsSection}
        slideImages={heroSlideImages}
        searchSectionRef={heroSearchRef}
      />
      <EventListings
        events={eventList || []}
        categories={categories}
        loading={loading}
        hasMore={hasMore}
        page={page}
        activeFilter={eventType || 'All'}
        country={country}
        countries={getCountryFeeListDetails || []}
        onCountryChange={handleCountryChange}
        onFilterChange={handleFilterChange}
        onLoadMore={handleLoadMore}
        onEventClick={handleEventClick}
        selectedCity={selectedCity}
        onClearCity={() => handleCityClick(selectedCity)}
        selectedDate={selectedDate}
        onClearDate={handleClearDate}
      />
      <DiscoverySection
        cities={trendingCities}
        country={country}
        selectedCity={selectedCity}
        onCityClick={handleCityClick}
        onExploreAll={handleExploreAll}
      />
      <SellerCTA />
      <AppPromo />
    </div>
  );
}
